/**
 * InDevelopment — Página placeholder para módulos ainda não implementados.
 *
 * Usada por módulos de negócio que existem na navegação mas cujas
 * funcionalidades serão construídas posteriormente (Estratégia, Comercial,
 * Financeiro, Projetos, Meta Ads, Instagram, Agentes, Estoque, Jurídico).
 * Mantém o layout padrão da
 * aplicação e a mensagem "Este módulo está em desenvolvimento." como
 * elemento principal, com uma composição visual discreta ao fundo que
 * remete a "em construção" (wireframes, cards, gráficos desfocados).
 */

import Layout from '../components/Layout.jsx'
import Icon from '../components/icons.jsx'

/* Ícone outline mais coerente com cada módulo. Fallback: settings. */
const MODULE_ICON = {
  'Estratégia': 'target',
  'Comercial': 'sales',
  'Financeiro': 'bank',
  'Projetos': 'page',
  'Atividades': 'chart',
  'Equipes': 'users',
  'Meta Ads': 'ads',
  'Instagram': 'meta',
  'Agentes': 'ai',
  'Estoque': 'inbox',
  'Jurídico': 'briefcase',
  'Contratos': 'edit',
  'Documentos': 'page',
  'Prospecção': 'search',
}

export default function InDevelopment({ title = 'Módulo', breadcrumb }) {
  const icon = MODULE_ICON[title] || 'settings'

  return (
    <Layout title={title} breadcrumb={breadcrumb}>
      <style>{`
        @keyframes dev-float {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-10px); }
        }
        @keyframes dev-pulse {
          0%,100% { opacity: .6; }
          50%     { opacity: 1; }
        }
        @keyframes dev-fade-up {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dev-dash {
          to { stroke-dashoffset: -120; }
        }
      `}</style>

      <div
        style={{
          position: 'relative',
          minHeight: '64vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          gap: 16,
          color: 'var(--ter)',
        }}
      >
        {/* ── Composição de fundo (baixa opacidade, apoio visual) ── */}
        <DevBackdrop />

        {/* ── Conteúdo principal ── */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            animation: 'dev-fade-up .5s ease both',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: 72,
              height: 72,
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface)',
              border: '1px solid var(--soft)',
              boxShadow: '0 8px 24px rgba(0,0,0,.06)',
              color: 'var(--text)',
              animation: 'dev-float 5s ease-in-out infinite',
            }}
          >
            <Icon n={icon} s={30} sw={1.4} />
            {/* Selo de "em construção" pulsante */}
            <span
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: 'var(--amber-bg)',
                border: '1.5px solid var(--amber-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--amber)',
                animation: 'dev-pulse 2s ease-in-out infinite',
              }}
            >
              <Icon n="settings" s={11} sw={2} />
            </span>
          </div>

          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em' }}>
            {title}
          </div>

          <p style={{ fontSize: 15, maxWidth: 380, lineHeight: 1.5, margin: 0, color: 'var(--sec)', fontWeight: 500 }}>
            Este módulo está em desenvolvimento.
          </p>

          <span
            style={{
              marginTop: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '6px 14px',
              borderRadius: 100,
              background: 'var(--bg)',
              border: '1px solid var(--soft)',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--ter)',
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--amber)',
                animation: 'dev-pulse 2s ease-in-out infinite',
              }}
            />
            Em construção
          </span>
        </div>
      </div>
    </Layout>
  )
}

/**
 * Plano de fundo decorativo: wireframes, cards, gráfico de barras, linha de
 * tendência e nós de conexão — tudo em outline e baixa opacidade. Puramente
 * estético; não interfere no conteúdo (pointerEvents: none).
 */
function DevBackdrop() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.1,
        maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 35%, transparent 85%)',
        WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 35%, transparent 85%)',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 800 480"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        stroke="var(--text)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Card wireframe — topo esquerdo */}
        <g style={{ animation: 'dev-float 7s ease-in-out infinite' }}>
          <rect x="60" y="60" width="190" height="120" rx="12" />
          <line x1="84" y1="92" x2="170" y2="92" />
          <line x1="84" y1="114" x2="226" y2="114" strokeWidth="1" />
          <line x1="84" y1="132" x2="200" y2="132" strokeWidth="1" />
          <rect x="84" y="150" width="64" height="16" rx="8" />
        </g>

        {/* Gráfico de barras — topo direito */}
        <g style={{ animation: 'dev-float 6s ease-in-out infinite', animationDelay: '.6s' }}>
          <rect x="560" y="50" width="190" height="150" rx="12" />
          <line x1="584" y1="172" x2="726" y2="172" strokeWidth="1" />
          <rect x="598" y="120" width="22" height="52" rx="3" />
          <rect x="636" y="96" width="22" height="76" rx="3" />
          <rect x="674" y="134" width="22" height="38" rx="3" />
          <rect x="712" y="84" width="14" height="88" rx="3" />
        </g>

        {/* Gráfico de linha / tendência — base esquerda */}
        <g style={{ animation: 'dev-float 8s ease-in-out infinite', animationDelay: '.3s' }}>
          <rect x="90" y="290" width="240" height="140" rx="12" />
          <polyline
            points="116,398 156,360 196,378 236,322 276,344 308,308"
            strokeWidth="2"
            strokeDasharray="120"
            style={{ animation: 'dev-dash 6s linear infinite' }}
          />
          <circle cx="156" cy="360" r="4" fill="var(--surface)" />
          <circle cx="236" cy="322" r="4" fill="var(--surface)" />
          <circle cx="308" cy="308" r="4" fill="var(--surface)" />
        </g>

        {/* Donut / progresso — base direita */}
        <g style={{ animation: 'dev-float 7s ease-in-out infinite', animationDelay: '.9s' }}>
          <circle cx="600" cy="350" r="62" />
          <path d="M600 288 a62 62 0 0 1 54 93" strokeWidth="6" />
          <circle cx="600" cy="350" r="34" strokeWidth="1" strokeDasharray="4 5" />
        </g>

        {/* Nós de conexão — formas geométricas e linhas */}
        <g strokeWidth="1" style={{ animation: 'dev-pulse 4s ease-in-out infinite' }}>
          <line x1="250" y1="120" x2="400" y2="230" />
          <line x1="400" y1="230" x2="560" y2="125" />
          <line x1="400" y1="230" x2="330" y2="360" />
          <line x1="400" y1="230" x2="538" y2="350" />
          <circle cx="400" cy="230" r="9" fill="var(--surface)" strokeWidth="1.5" />
          <circle cx="250" cy="120" r="5" fill="var(--surface)" />
          <circle cx="560" cy="125" r="5" fill="var(--surface)" />
          <circle cx="330" cy="360" r="5" fill="var(--surface)" />
          <circle cx="538" cy="350" r="5" fill="var(--surface)" />
        </g>
      </svg>
    </div>
  )
}
